import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Input, Tooltip } from '@mm/components';
import {
  FileOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import css from './FacebookSettingsStep.module.css';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useXRayConfigurationCRUDActions } from '@mm/state/actions/XRayConfiguration';
import { selectXRayConfig } from '@mm/state/selectors/xrayConfigSelectors';
import { emptyEventsConfig } from '../SetupForm';
import { Spin } from 'antd';
import { isEqual } from 'lodash';

export const FacebookSettingsStep = ({
  configReady,
  isDisabled,
}: {
  configReady?: boolean;
  isDisabled: boolean;
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const history = useHistory();
  const disabled = !configReady || isDisabled;

  const xRayConfigurationCRUDActions = useXRayConfigurationCRUDActions();

  const defaultConfiguration = useSelector(selectXRayConfig);

  const [busy, setBusy] = useState(false);

  const [fbPixelId, setFbPixelId] = useState(
    defaultConfiguration?.pixelId || '',
  );
  const [fbAccessToken, setFbAccessToken] = useState(
    defaultConfiguration?.accessToken || '',
  );
  const [fbApiVersion, setFbApiVersion] = useState('');
  const hasChanges = !isEqual(fbPixelId, defaultConfiguration?.pixelId);

  const handleSave = async () => {
    setBusy(true);

    try {
      const res =
        await xRayConfigurationCRUDActions.updateXRayEventsConfiguration(
          chatbotId,
          {
            ...defaultConfiguration,
            configurations:
              defaultConfiguration?.configurations || emptyEventsConfig,
            pixelId: fbPixelId,
            accessToken: fbAccessToken,
          },
        );
      if (res.accessToken && res.pixelId) {
        history.replace(
          history.location.pathname.replace('/facebook-settings', '/start'),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip title={disabled ? 'Complete previous step to continue' : ''}>
      <div
        className={classNames(css.wrapper, {
          [css.disabledWrapper]: disabled,
        })}
      >
        <div className={css.header}>
          <div className={css.stepContentHeaderText}>
            <span className={css.stepContentTitle}>Facebook Settings</span>
            <span className={css.stepContentDescription}>
              In order to send your transactions data to this destination, we
              need the following information from your Facebook account.
            </span>
          </div>

          <Button
            css={{ container: css.submitBtn }}
            onClick={handleSave}
            disabled={
              busy || [fbPixelId, fbAccessToken].some(v => !v) || !hasChanges
            }
          >
            Save
            {busy && (
              <Spin
                indicator={
                  <LoadingOutlined className={css.submitBtnSpinner} spin />
                }
              />
            )}
          </Button>
        </div>
        <div className={css.card}>
          <div className={css.field}>
            <div className={css.fieldName}>
              <span>Meta Pixel ID</span>
              <Tooltip
                title={
                  <div>
                    {' '}
                    Learn more about finding your Meta Pixel ID
                    <a className={css.learnMoreLink} href="https://google.com">
                      Learn more
                    </a>
                  </div>
                }
                placement="bottom"
              >
                <QuestionCircleOutlined className={css.infoIcon} />
              </Tooltip>
            </div>

            <Input
              onChange={e => setFbPixelId(e.target.value.trim())}
              disabled={busy}
              value={fbPixelId}
              type="text"
              placeholder="Add Meta Pixel ID"
              css={{ container: css.input }}
            />
          </div>

          <div className={css.field}>
            <div className={css.fieldName}>
              <span>Conversion API Token</span>
              <Tooltip
                title={
                  <div>
                    {' '}
                    Learn more about finding your Conversion API Token
                    <a className={css.learnMoreLink} href="https://google.com">
                      Learn more
                    </a>
                  </div>
                }
                placement="bottom"
              >
                <QuestionCircleOutlined className={css.infoIcon} />
              </Tooltip>
            </div>

            <Input
              onChange={e => setFbAccessToken(e.target.value.trim())}
              disabled={busy}
              value={fbAccessToken}
              placeholder="Add Conversion API Token"
              type="text"
              css={{ container: css.input }}
            />
          </div>

          <div className={classNames(css.field, css.hidden)}>
            <div className={css.fieldContainer}>
              <div className={css.fieldName}>
                <span>Facebook API Version</span>
                <Tooltip
                  title="Facebook API Version Description"
                  placement="bottom"
                >
                  <QuestionCircleOutlined className={css.infoIcon} />
                </Tooltip>
              </div>
              <a href="" className={css.whichVersionLink}>
                <FileOutlined />
                Which version should I choose?
              </a>
            </div>
            <Input
              onChange={e => setFbApiVersion(e.target.value.trim())}
              disabled={busy}
              value={fbApiVersion}
              type="text"
              placeholder="Add Facebook API Version"
              css={{ container: css.input }}
            />
          </div>
        </div>
      </div>
    </Tooltip>
  );
};
